/**
 * 国家国旗工具
 *
 * 统一国旗图标解析优先级：
 *   1. /api/home/startup 下发的 countries[].displayIcon（自定义图标资源 ID）
 *   2. /api/home_display/config 下发的 redirectConfigs[].countryIcon（按 item 解析时可用）
 *   3. flag-icons CDN 的 1:1 SVG 国旗
 *   4. 没有有效国家码时返回空串（调用方走占位符）
 */

import type { Country } from "../modules/startup/types";
import type { RedirectConfigItem } from "../modules/homeDisplay/types";
import utils from "./utils";

/** flag-icons 1:1 国旗图 CDN（与 flag-icons 库同源） */
export const FLAG_ICONS_CDN =
  "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/flags/1x1";

/** 特殊国家 ID 映射（业务侧 ID → flag-icons 两位标准码） */
const COUNTRY_ID_FLAG_ALIAS: Record<string, string> = {
  IDN: "id",
  PH001: "ph",
  PH002: "ph",
  PH003: "ph",
};

/**
 * 从业务国家 ID（或 countryRedirectId）提取 flag-icons 两位国家码（小写）。
 * 优先走 COUNTRY_ID_FLAG_ALIAS 别名表，否则取前两位。
 * 如：PH001 → ph、IDN → id、US → us。
 */
export function getCountryCodeFromId(
  countryIdOrRedirect: string | null | undefined
): string | null {
  const raw = (countryIdOrRedirect ?? "").toString().trim();
  if (!raw) return null;
  const alias = COUNTRY_ID_FLAG_ALIAS[raw.toUpperCase()];
  if (alias) return alias;
  return raw.length >= 2 ? raw.slice(0, 2).toLowerCase() : null;
}

/** 从 RedirectConfigItem 取两位国家码（小写） */
export function getCountryCodeForFlag(item: RedirectConfigItem): string | null {
  return getCountryCodeFromId(item.countryRedirectId);
}

/** 构建 startup.countries 的 displayIcon 映射：countryId(大写) → displayIcon 资源 ID */
export function buildDisplayIconMap(
  countries: Country[] | undefined | null
): Record<string, string> {
  const map: Record<string, string> = {};
  (countries ?? []).forEach(c => {
    if (c?.id && c.displayIcon) {
      map[c.id.trim().toUpperCase()] = c.displayIcon;
    }
  });
  return map;
}

/**
 * 根据国家码 + displayIcon 映射，解析国旗图片 URL。
 * 优先级：startup.displayIcon → countryIconFallback（如 redirectConfigs.countryIcon）→ flag-icons CDN。
 * 没有 code 时返回空串。
 */
export function resolveFlagSrcByCode(
  code: string | null | undefined,
  displayIconMap: Record<string, string>,
  countryIconFallback?: string | null
): string {
  if (!code) return "";
  const displayIconId = displayIconMap[code.toUpperCase()];
  const displayIconUrl = displayIconId ? utils.getImageUrl(displayIconId) : "";
  if (displayIconUrl) return displayIconUrl;
  const countryIconUrl = countryIconFallback
    ? utils.getImageUrl(countryIconFallback)
    : "";
  if (countryIconUrl) return countryIconUrl;
  return `${FLAG_ICONS_CDN}/${code}.svg`;
}

/**
 * 便捷方法：基于 RedirectConfigItem 直接拿国旗 URL。
 * 自动把 item.countryIcon 作为第二顺位回退。
 */
export function resolveFlagSrcByItem(
  item: RedirectConfigItem,
  displayIconMap: Record<string, string>
): string {
  return resolveFlagSrcByCode(
    getCountryCodeForFlag(item),
    displayIconMap,
    item.countryIcon
  );
}
